import { useRouter } from '@application/utils';
import type { BreedDetailsInfoFragment } from '@gql/graphql';
import { DogBreedImgType } from '@gql/graphql';
import type { BreedPageProps } from '@presentation/pages';
import { useMemo } from 'react';

export const useFormatBreed = (breed?: BreedDetailsInfoFragment | null) => {
  const { translate, language } = useRouter();

  const translation = useMemo(() => {
    if (!breed?.translations.length) return;
    return breed?.translations?.find((x) => x.language === language) ?? undefined;
  }, [breed?.translations, language]);

  const formattedBreed: Partial<BreedPageProps> = useMemo(() => {
    if (!breed) return {};
    return {
      title: translation?.name,
      description: translation?.description,
      images: {
        profile: (breed.imgs ?? []).find((img) => img.type === DogBreedImgType.MAIN)?.medium,
        background: (breed.imgs ?? []).find((img) => img.type === DogBreedImgType.COVER)?.large,
      },
      details: [
        {
          label: translate('glossary.otherNames'),
          value: translation?.otherNames.length ? translation?.otherNames.join(', ') : undefined,
        },
        {
          label: translate('glossary.fciGroup'),
          value: breed.fciGroup ? translate(`enum.breedFciGroupType.${breed.fciGroup}`) : undefined,
        },
        {
          label: translate('glossary.hair'),
          value: breed.hairType
            ? translate(`enum.hairType.${breed.hairType.replace('_', '')}`)
            : undefined,
        },
        {
          label: translate('glossary.head'),
          value: breed.headShape ? translate(`enum.headShape.${breed.headShape}`) : undefined,
        },
      ],
      attributeCategories: [
        {
          categoryName: translate('breedDetails.fragilities'),
          attributes: [
            { label: translate('glossary.robustness'), value: breed.robustnessIndex },
            { label: translate('glossary.maintenance'), value: breed.maintenanceIndex },
            { label: translate('glossary.hairLoss'), value: breed.hairLossIndex },
            {
              label: translate('glossary.supportsHeat'),
              value: breed.heatToleranceIndex,
            },
            {
              label: translate('glossary.supportsCold'),
              value: breed.coldToleranceIndex,
            },
          ],
        },
        {
          categoryName: translate('breedDetails.advices'),
          attributes: [
            {
              label: translate('glossary.predatoryInstinct'),
              value: breed.predatoryInstinctIndex,
            },
            { label: translate('glossary.independent'), value: breed.independenceIndex },
            {
              label: translate('glossary.barkingTendency'),
              value: breed.barkingTendancyIndex,
            },
            {
              label: translate('glossary.foreignersSociability'),
              value: breed.foreignerSociabilityIndex,
            },
            {
              label: translate('glossary.needPhysicalActivities'),
              value: breed.physicalActivityNeedIndex,
            },
          ],
        },
      ],
      content: {
        synologyRank: translation?.synologyRank,
        historic: translation?.historic,
        morphology: translation?.morphology,
        fragilities: translation?.fragilities,
        advices: translation?.advices,
      },
      genetics:
        translation?.genetics && translation?.geneticAnalyzeType
          ? {
              text: translate(
                `breedDetails.genetics.subtitle.${translation?.geneticAnalyzeType}`,
              ).replace('$breed', translation?.name ?? ''),
              items: translation?.genetics,
            }
          : undefined,
    } as Partial<BreedPageProps>;
  }, [
    breed,
    translate,
    translation?.advices,
    translation?.description,
    translation?.fragilities,
    translation?.geneticAnalyzeType,
    translation?.genetics,
    translation?.historic,
    translation?.morphology,
    translation?.name,
    translation?.otherNames,
    translation?.synologyRank,
  ]);

  const imagesToPreload = useMemo(() => {
    return [formattedBreed.images?.background, formattedBreed.images?.profile].filter(
      (x) => !!x,
    ) as string[];
  }, [formattedBreed.images?.background, formattedBreed.images?.profile]);

  return { formattedBreed, imagesToPreload };
};
